import React, { useState, useRef, useEffect } from "react";
import "./index.css";
import { LawIcon } from "./components/Icons.tsx";
import { ChatMessage } from "./components/ChatMessage.tsx";
import { SearchHeader } from "./components/SearchHeader.tsx";
import { Sidebar } from "./components/Sidebar.tsx";
import { useChat } from "./hooks/useChat.ts";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { PhaseIndicator } from "./components/PhaseIndicator.tsx";
import { ChatInput } from "./components/ChatInput.tsx";
import { CookieBanner } from "./components/CookieBanner.tsx";

// Detect if the current viewport is mobile-sized
const isMobileViewport = () => typeof window !== "undefined" && window.innerWidth < 768;

export function App() {
  const {
    query,
    setQuery,
    messages,
    isLoading,
    phase,
    isSearched,
    handleSubmit,
    sessionId,
    handleSelectSession,
    selectedFile,
    setSelectedFile,
  } = useChat();

  // Default closed on mobile, open on desktop
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobileViewport());
  const endOfMessagesRef = useRef<HTMLDivElement>(null);

  // Collapse sidebar when crossing the mobile breakpoint on resize
  useEffect(() => {
    const onResize = () => {
      if (isMobileViewport()) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Close sidebar on mobile when a session is selected
  const handleSessionSelect = (id: string | null) => {
    handleSelectSession(id);
    if (isMobileViewport()) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div className="flex h-[100dvh] overflow-hidden selection:bg-blue-100 dark:selection:bg-blue-900/30 font-sans bg-white dark:bg-slate-950">
      {/* Background decoration */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute inset-0 bg-mesh opacity-60 dark:opacity-40" />
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-500/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-indigo-500/10 blur-[120px]" />
      </div>

      {/* Mobile overlay backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 backdrop-blur-sm md:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — fixed overlay on mobile, static column on desktop */}
      <div className={`
        fixed md:static inset-y-0 left-0 z-30
        transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        ${isSidebarOpen ? "md:flex" : "hidden md:hidden"}
      `}
      >
        <Sidebar
          currentSessionId={sessionId}
          onSelectSession={handleSessionSelect}
          isOpen={isSidebarOpen}
          setIsOpen={setIsSidebarOpen}
        />
      </div>

      {/* Main Content Area */}
      <div className={`flex flex-col flex-1 min-w-0 h-full relative ${isSearched ? "overflow-hidden" : "overflow-y-auto"}`}>
        <SearchHeader
          query={query}
          setQuery={setQuery}
          handleSubmit={handleSubmit}
          isSearched={isSearched}
          isLoading={isLoading}
          onMenuClick={!isSidebarOpen ? () => setIsSidebarOpen(true) : undefined}
          onLogoClick={() => handleSelectSession(null)}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
        />

        {/* Chat Messages */}
        {isSearched && (
          <>
            <main className="flex-1 overflow-y-auto w-full px-3 sm:px-6 pt-4 pb-32">
              <div className="max-w-4xl mx-auto">
                <ErrorBoundary>
                  <div className="space-y-6 sm:space-y-10">
                    {messages.map(msg => (
                      <div key={msg.id} className="animate-message">
                        <ChatMessage message={msg} />
                      </div>
                    ))}

                    {/* Loading State */}
                    {isLoading && (
                      <div className="flex gap-4 sm:gap-6 animate-message">
                        <div className="shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-2xl glass-panel flex items-center justify-center text-blue-600 dark:text-blue-400">
                          <LawIcon />
                        </div>
                        <PhaseIndicator phase={phase} />
                      </div>
                    )}

                    <div ref={endOfMessagesRef} className="h-4" />
                  </div>
                </ErrorBoundary>
              </div>
            </main>

            {/* Bottom Fixed Input */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-slate-950 dark:via-slate-950/95 pb-6 pt-12 pointer-events-none">
              <div className="max-w-3xl mx-auto w-full pointer-events-auto">
                <ChatInput
                  query={query}
                  setQuery={setQuery}
                  handleSubmit={handleSubmit}
                  isLoading={isLoading}
                  isSearched={isSearched}
                  selectedFile={selectedFile}
                  setSelectedFile={setSelectedFile}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <CookieBanner />
    </div>
  );
}
